import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChatFill, LikeFill, LikeLine } from '@peacecover/icon';
import { useGetCommentDetail } from '@pocapan/shared/domains/scene/ui/hooks/useGetCommentDetail';
import { useLikeAdaptedFictionEditionComment } from '@pocapan/shared/domains/scene/ui/hooks/useLikeAdaptedFictionEditionComment';
import { formatDateTime } from '@pocapan/shared/utils/DateUtil';
import { CastRelationshipBadge } from './CastRelationshipBadge';
import { CommentAuthorAvatar } from './CommentAuthorAvatar';

interface CommentDetailParentCommentItemProps {
  adaptedFictionEditionId: string;
  commentId: string;
}

export const CommentDetailParentCommentItem = ({
  adaptedFictionEditionId,
  commentId
}: CommentDetailParentCommentItemProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: commentDetail } = useGetCommentDetail({
    adaptedFictionEditionId,
    commentId
  });

  const { data: parentCommentDetail } = useGetCommentDetail({
    adaptedFictionEditionId,
    commentId: commentDetail?.parentId ?? ''
  });

  const { mutateAsync: likeAdaptedFictionEditionComment } = useLikeAdaptedFictionEditionComment({
    adaptedFictionEditionId,
    commentId: commentDetail?.commentId ?? ''
  });

  const handleLikeAdaptedFictionEditionComment = async () => {
    if (!commentDetail?.commentId) return;
    await likeAdaptedFictionEditionComment();
  };

  return (
    <div className="mt-[10px]">
      <div className="px-[24px]">
        {commentDetail?.parentId && (
          <div className="flex items-center gap-[10px] rounded-[6px] bg-surface-lv2 py-[8px] pl-[23px] w-full mb-[12px]">
            <ChatFill size={14} className="text-text-main-lv4" />
            <p className="text-[12px] leading-[14px] text-text-main-lv4">
              <span className="font-semibold max-w-[100px] truncate">
                {parentCommentDetail?.content}
              </span>
              {t('SceneCommentsDetail.replyDescription')}
            </p>
          </div>
        )}

        <div className="flex items-center gap-[10px] mb-[12px]">
          <button
            type="button"
            onClick={() => navigate(`/feed/user_profile/${commentDetail?.userProfileId}`)}
          >
            <CommentAuthorAvatar
              anonymous={commentDetail?.anonymous}
              thumbnailUrl={commentDetail?.authorThumbnailUrl ?? undefined}
              size={34}
            />
          </button>
          <div className="flex flex-col justify-between gap-[2px] items-start">
            <p className="text-[15px] font-semibold leading-[18px] text-text-main-lv2">
              {commentDetail?.authorName}
            </p>
            <p className="text-[12px] leading-[14px] text-text-main-lv4">
              {commentDetail?.authoredAt} ·{' '}
              {formatDateTime(commentDetail?.createdAt ?? new Date(), 'yyyy.MM.dd')}
            </p>
          </div>
        </div>

        <CastRelationshipBadge
          castRelationship={commentDetail?.castRelationship}
          className="mt-[12px] hidden"
        />

        <p className="text-[16px] leading-[22px] text-text-main-lv2 mt-[4px]">
          {commentDetail?.content}
        </p>

        <button
          type="button"
          className="mt-[13px]"
          onClick={handleLikeAdaptedFictionEditionComment}
          aria-label="좋아요"
        >
          {commentDetail?.isLiked ? (
            <LikeFill size={30} className="text-warning" />
          ) : (
            <LikeLine size={30} className="text-text-sub-lv1" />
          )}
        </button>
      </div>

      <div className="border-t border-separator2 px-[24px] pt-[16px]">
        <div className="flex items-center py-[4px] gap-[6px] text-[13px] font-medium leading-[16px]">
          <p className="text-text-main-lv4">
            {t('SceneCommentsDetail.comment')}{' '}
            <span className="text-text-main-lv3">{commentDetail?.commentsCount}</span>
          </p>
          <p className="text-text-main-lv3">·</p>
          <p className="text-text-main-lv4">
            {t('SceneCommentsDetail.like')}{' '}
            <span className="text-text-main-lv3">{commentDetail?.likesCount}</span>
          </p>
        </div>
      </div>
    </div>
  );
};
